import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  signal,
  ViewChild,
  AfterViewInit,
  ElementRef,
  input,
  inject,
  ViewEncapsulation,
  model,
} from '@angular/core';
import { PalantirConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { PalantirTextComponent } from '../text/text.component';
import { SuggestionListComponent } from './components/suggestion-list/suggestion-list.component';
import { TemplateVariable } from './interfaces/template-variable.interface';
import { templateEditorLogger } from './logger.instance';
import { AstParserService } from './services/ast-parser.service';
import { CursorManagementService } from './services/cursor-management.service';
import { DomRendererService } from './services/dom-renderer.service';
import { ElementInsertionService } from './services/element-insertion.service';
import { KeyboardService } from './services/keyboard.service';
import { SuggestionService, VariableProvider } from './services/suggestion.service';
import { TemplateEditorService } from './services/template-editor.service';
import { UndoRedoService } from './services/undo-redo.service';
import { SELECTORS, CLASSES } from './utils/constants';

const logger = templateEditorLogger.extend('component');

/**
 * Componente principal do editor de templates.
 * Orquestra os serviços de edição, sugestão e histórico (undo/redo)
 * para fornecer uma experiência de edição de templates rica e interativa.
 */
@Component({
  selector: 'fx-template-editor',
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TemplateEditorService,
    SuggestionService,
    UndoRedoService,
    AstParserService,
    CursorManagementService,
    DomRendererService,
    ElementInsertionService,
    KeyboardService,
  ],
  imports: [SuggestionListComponent, NgClass, PalantirTextComponent],
  encapsulation: ViewEncapsulation.None,
})
export class FxTemplateEditorComponent implements AfterViewInit {
  /**
   * Referência ao elemento `div[contenteditable]` do editor.
   */
  @ViewChild('editor') public editorRef!: ElementRef<HTMLDivElement>;

  // --- Entradas do Componente (Inputs) ---

  /**
   * Provedor de variáveis. Pode ser um array estático ou uma função de busca assíncrona.
   */
  public variables = input.required<VariableProvider>({ alias: 'variables' });

  /**
   * Caracteres que acionam o dropdown de sugestões (ex: '{{', '/').
   */
  public triggerChars = input<string[]>(['{{', '/'], { alias: 'triggerChars' });

  /**
   * Emite todas as vezes que o valor do template é alterado.
   */
  public value = model<string>('');

  /**
   * Caso verdadeiro, o editor será somente leitura.
   */
  public readOnly = input<boolean>(false, { alias: 'readOnly' });

  /**
   * Caso verdadeiro, o editor não irá aceitar quebras de linha.
   */
  public noLineBreaks = input<boolean>(false, { alias: 'noLineBreaks' });

  /**
   * Se verdadeiro, exibe o editor visual rico (contenteditable). Caso falso, oculta o editor visual.
   */
  public showRichEditor = input<boolean>(true);

  /**
   * Se verdadeiro, exibe o editor de texto para edição direta do código Handlebars. Caso falso, oculta o editor de texto.
   */
  public showTextEditor = input<boolean>(true);

  // --- Serviços ---

  /**
   * Serviço que gerencia o estado do editor, o parsing e a renderização da AST.
   */
  private editorService = inject(TemplateEditorService);

  /**
   * Serviço que gerencia a lógica do dropdown de sugestões.
   */
  private suggestionService = inject(SuggestionService);

  /**
   * Serviço que gerencia o modal de confirmaç˜ão de exclusão.
   */
  private confirmationDialogService = inject(PalantirConfirmDialogService);

  /**
   * Serviço que gerencia os atalhos de teclado.
   */
  private keyboardService = inject(KeyboardService);

  // --- Exposição dos Sinais dos Serviços para o Template ---

  /**
   * O valor do template compilado como string.
   */
  public inputValue = this.editorService.inputValue;

  /**
   * A lista de sugestões a ser exibida.
   */
  public suggestions = this.suggestionService.suggestions;

  /**
   * Sinaliza se a busca por sugestões está em andamento.
   */
  public isLoading = this.suggestionService.isLoading;

  /**
   * Controla a visibilidade do dropdown de sugestões.
   */
  public isDropdownVisible = this.suggestionService.isDropdownVisible;

  /**
   * O índice da sugestão ativa (destacada).
   */
  public activeIndex = this.suggestionService.activeIndex;

  /**
   * As coordenadas (top, left) para posicionar o dropdown.
   */
  public dropdownPosition = this.suggestionService.dropdownPosition;

  /**
   * ID único para associar `label` e `input` para acessibilidade.
   */
  public readonly uniqueId = Math.random().toString(36).slice(2);

  // --- Estado do Componente para Exclusão ---

  /**
   * O elemento "chip" ou bloco atualmente selecionado pelo usuário.
   */
  public selectedElement = signal<HTMLElement | null>(null);

  /**
   * Armazena o elemento a ser excluído após a confirmação.
   */
  private elementToDelete = signal<HTMLElement | null>(null);

  // --- Estado para Sincronização Bidirecional ---
  private isUpdatingRichEditor = false;

  private textareaDebounce: ReturnType<typeof setTimeout> | null = null;

  public constructor() {
    // Efeito para adicionar/remover a classe 'is-selected' do elemento selecionado.
    // Isso fornece um feedback visual claro para o usuário.
    effect((onCleanup) => {
      const selected = this.selectedElement();
      // Garante que a referência ao editor exista antes de usá-la.
      // Limpa seleções anteriores para evitar múltiplos elementos selecionados.
      if (this.editorRef?.nativeElement) {
        const selectedElements = this.editorRef.nativeElement.querySelectorAll(
          `.${CLASSES.IS_SELECTED}`,
        );
        for (const el of Array.from(selectedElements)) el.classList.remove(CLASSES.IS_SELECTED);
      }

      if (selected) {
        selected.classList.add(CLASSES.IS_SELECTED);
        // Define uma função de limpeza para remover a classe quando o efeito for reexecutado
        // ou quando o componente for destruído.
        onCleanup(() => {
          selected.classList.remove(CLASSES.IS_SELECTED);
        });
      }
    });
  }

  /**
   * Após a view ser inicializada, configura os serviços com os dados de entrada.
   */
  public ngAfterViewInit(): void {
    logger.debug('ngAfterViewInit', { value: this.value(), readOnly: this.readOnly() });
    const provider = this.variables();
    this.suggestionService.initialize(provider);

    const initialVars = Array.isArray(provider) ? provider : [];
    this.editorService.initialize(this.editorRef.nativeElement, initialVars, this.value, {
      readOnly: this.readOnly(),
      noLineBreaks: this.noLineBreaks(),
    });
  }

  /**
   * Lida com cliques dentro do editor para gerenciar a seleção de componentes e a exclusão.
   *
   * @param event O evento do mouse.
   */
  public onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    logger.debug('onClick', { target });

    // Se o clique foi em um botão de exclusão, processa a exclusão e interrompe.
    if (this._handleDeleteClick(event, target)) {
      return;
    }

    // Gerencia a seleção de "chips" ou blocos.
    this._handleComponentSelection(target);
  }

  /**
   * Inicia o processo de exclusão de um elemento.
   * Para blocos, exibe um modal de confirmação. Para outros elementos, exclui diretamente.
   *
   * @param element O elemento a ser excluído.
   */
  public async requestDelete(element: HTMLElement): Promise<void> {
    logger.debug('requestDelete', { element });
    if (element.matches(SELECTORS.BLOCK_STATEMENT)) {
      this.elementToDelete.set(element);
      const dialogRef = this.confirmationDialogService.openDialog({
        title: 'Confirmar Exclusão',
        text: 'Você tem certeza que deseja excluir este bloco? Todo o conteúdo dentro dele também será removido.',
        cancelText: 'Cancelar',
        successText: 'Excluir',
        successVariant: 'negative',
      });
      const result = await dialogRef.afterClosed();
      if (result?.role === 'success') {
        logger.debug('requestDelete: confirmed');
        this.confirmDelete();
      } else {
        logger.debug('requestDelete: cancelled');
        this.cancelDelete();
      }

      return;
    }

    this.editorService.deleteElement(element);
    this.selectedElement.set(null);
  }

  /**
   * Confirma e executa a exclusão do elemento armazenado em `elementToDelete`.
   */
  public confirmDelete(): void {
    const element = this.elementToDelete();
    if (element) {
      this.editorService.deleteElement(element);
    }

    this.cancelDelete();
  }

  /**
   * Cancela a operação de exclusão, fechando o modal e limpando o estado.
   */
  public cancelDelete(): void {
    this.elementToDelete.set(null);
    this.selectedElement.set(null); // Desseleciona ao cancelar
  }

  /**
   * Lida com o evento `input` do editor visual.
   * Aciona a busca por sugestões, atualiza placeholders e o valor do template no serviço.
   */
  public onInput(): void {
    if (this.isUpdatingRichEditor || this.readOnly()) {
      return;
    }

    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      this.suggestionService.closeDropdown();
      return;
    }

    // Determina o contexto de busca (editor principal ou um argumento de função).
    const { searchContextNode, argumentSpan } = this._getSearchContext(selection);
    logger.debug('onInput', { searchContextNode, argumentSpan });
    if (!searchContextNode) {
      this.suggestionService.closeDropdown();
      return;
    }

    // Atualiza o estado visual do placeholder do argumento.
    this._updateArgumentPlaceholder(argumentSpan);

    // Obtém o texto antes do cursor para encontrar gatilhos de sugestão.
    const range = selection.getRangeAt(0);
    const textBeforeCursor = this.editorService.getTextBeforeCursor(searchContextNode, range);

    // Verifica se um gatilho foi digitado e aciona o dropdown.
    this._handleSuggestionTrigger(textBeforeCursor, argumentSpan ?? undefined);

    // Se o dropdown estiver visível, atualiza sua posição.
    if (this.isDropdownVisible()) {
      this.updateDropdownPosition();
    }

    // Atualiza o valor do template e agenda um salvamento no histórico.
    const newValue = this.editorService.parseEditorContent();
    logger.debug('onInput: parsed new value', { newValue });
    this.editorService.inputValue.set(newValue);
    this.editorService.scheduleSaveState();
  }

  /**
   * Lida com a entrada de texto na `textarea` para sincronização bidirecional.
   *
   * @param event O evento de input da textarea.
   */
  public onTextareaInput(event: Event): void {
    const newValue = (event.target as HTMLTextAreaElement).value;
    logger.debug('onTextareaInput', { newValue });
    // Atualiza o sinal no serviço imediatamente para que a textarea seja responsiva.
    this.editorService.inputValue.set(newValue);

    if (this.textareaDebounce) {
      clearTimeout(this.textareaDebounce);
      this.textareaDebounce = null;
    }

    this.textareaDebounce = setTimeout(() => {
      // Define uma flag para prevenir que a atualização programática do editor visual
      // dispare seu próprio evento `onInput`, causando um loop.
      this.isUpdatingRichEditor = true;

      // Chama o serviço para re-renderizar o editor visual a partir do texto.
      logger.debug('onTextareaInput: updating rich editor');
      this.editorService.updateRichEditorFromText(newValue);

      // Reseta a flag de forma assíncrona, após o DOM ser atualizado e
      // o evento de input (ignorado) ter sido processado.
      setTimeout(() => {
        this.isUpdatingRichEditor = false;
      }, 0);
    }, 400); // Debounce de 400ms para a re-renderização
  }

  /**
   * Lida com o evento `keydown` para atalhos de teclado (undo/redo, exclusão)
   * e navegação na lista de sugestões.
   *
   * @param event O evento do teclado.
   */
  public onKeyDown(event: KeyboardEvent): void {
    if (this.readOnly()) return;

    if (this.keyboardService.handleUndoRedo(event)) return;
    if (
      this.keyboardService.handleDeleteSelected(
        event,
        this.selectedElement(),
        this.isDropdownVisible(),
        async (el) => this.requestDelete(el),
      )
    )
      return;

    if (this.isDropdownVisible()) {
      this.keyboardService.handleSuggestionNavigation(
        event,
        this.suggestions(),
        this.activeIndex(),
        (idx) => this.activeIndex.set(idx),
        (item) => this.selectItem(item),
        () => this.suggestionService.closeDropdown(),
      );
      return;
    }

    this.keyboardService.handleTextAndChipSelection(event, this.editorRef.nativeElement);

    if (event.key === 'Enter' && this.noLineBreaks()) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Backspace' && !this.isDropdownVisible()) {
      this.editorService.handleBackspace(event, () => {
        this.editorService.inputValue.set(this.editorService.parseEditorContent());
        this.editorService.saveState();
      });
    }
  }

  /**
   * Insere o item selecionado da lista de sugestões no editor.
   *
   * @param item A variável de template selecionada.
   */
  public selectItem(item: TemplateVariable): void {
    const trigger = this.suggestionService.trigger();
    const filterTerm = this.suggestionService.filterTerm();
    const triggerContext = trigger?.context;

    logger.debug('selectItem', { item, trigger, filterTerm });

    if (item.type === 'block-helper') {
      this.editorService.insertBlock(item, filterTerm, trigger!);
    } else if (triggerContext) {
      // Inserindo em um argumento
      this.editorService.insertVariableIntoArgument(item, triggerContext, filterTerm, trigger!);
    } else {
      // Inserindo no corpo principal
      this.editorService.insertMustache(item, filterTerm, trigger!);
    }

    // Após a inserção, atualiza o valor, salva o estado e fecha o dropdown.
    this.editorService.inputValue.set(this.editorService.parseEditorContent());
    this.editorService.saveState();
    this.suggestionService.closeDropdown();
    this.editorRef.nativeElement.focus();
  }

  /**
   * Lida com o evento `blur` do editor.
   * Fecha o dropdown e limpa a seleção para evitar estados persistentes indesejados.
   */
  public handleBlur(): void {
    // Atraso para permitir cliques no dropdown ou em botões de exclusão antes de fechar/limpar.
    setTimeout(() => {
      if (!this.editorRef.nativeElement.contains(document.activeElement)) {
        this.suggestionService.closeDropdown();
        this.selectedElement.set(null); // Desseleciona ao sair do editor
      }
    }, 200);
  }

  // --- Métodos Auxiliares Privados ---

  /**
   * Processa cliques em botões de exclusão.
   *
   * @returns `true` se um clique de exclusão foi tratado.
   */
  private _handleDeleteClick(event: MouseEvent, target: HTMLElement): boolean {
    const deleteButton = target.closest<HTMLElement>(SELECTORS.DELETE_ACTION);
    if (deleteButton) {
      event.preventDefault(); // Previne que o editor perca o foco
      event.stopPropagation(); // Previne o evento de `onClick` do componente
      const componentEl = deleteButton.closest<HTMLElement>(
        `${SELECTORS.CHIP_WRAPPER}, ${SELECTORS.BLOCK_STATEMENT}`,
      );
      if (componentEl) {
        void this.requestDelete(componentEl);
      }

      return true;
    }

    return false;
  }

  /**
   * Gerencia a seleção de um "chip" ou bloco ao ser clicado.
   */
  private _handleComponentSelection(target: HTMLElement): void {
    // Se o clique for em uma área de edição de texto (argumento, programa de bloco),
    // não selecione o componente pai. Isso permite a edição do conteúdo.
    if (target.isContentEditable && target !== this.editorRef.nativeElement) {
      this.selectedElement.set(null);
      return;
    }

    const componentEl = target.closest<HTMLElement>(
      `${SELECTORS.CHIP_WRAPPER}, ${SELECTORS.BLOCK_STATEMENT}`,
    );
    this.selectedElement.set(componentEl);
  }

  /**
   * Determina o nó de contexto para a busca de sugestões.
   * Pode ser o editor principal ou um `span` de argumento de função.
   *
   * @param selection A seleção atual do `window`.
   * @returns O nó de contexto e o span do argumento, se aplicável.
   */
  private _getSearchContext(selection: Selection): {
    argumentSpan: HTMLElement | null;
    searchContextNode: HTMLElement | null;
  } {
    const focusNode = selection.focusNode;
    if (!focusNode || !this.editorRef.nativeElement.contains(focusNode)) {
      return { searchContextNode: null, argumentSpan: null };
    }

    // O nó editável atual é o elemento pai do nó de texto focado.
    const currentEditable = (
      focusNode.nodeType === Node.ELEMENT_NODE ? focusNode : focusNode.parentElement
    ) as HTMLElement;
    const argumentSpan = currentEditable.closest<HTMLElement>(SELECTORS.ARG_NAME);
    // Se estiver dentro de um argumento, o contexto é o argumento. Senão, é o editor.
    return { searchContextNode: argumentSpan ?? this.editorRef.nativeElement, argumentSpan };
  }

  /**
   * Adiciona ou remove a classe `is-placeholder` de um `span` de argumento
   * com base em seu conteúdo.
   */
  private _updateArgumentPlaceholder(argumentSpan: HTMLElement | null): void {
    if (argumentSpan) {
      const text = argumentSpan.textContent ?? '';
      if (text && text !== argumentSpan.dataset['argName']) {
        argumentSpan.classList.remove(CLASSES.IS_PLACEHOLDER);
      } else if (!text) {
        // Se o usuário apagar todo o conteúdo, restaura o placeholder.
        argumentSpan.classList.add(CLASSES.IS_PLACEHOLDER);
      }
    }
  }

  /**
   * Verifica se o texto antes do cursor contém um gatilho de sugestão válido.
   *
   * @param textBeforeCursor O texto a ser analisado.
   * @param context O `HTMLElement` do argumento, se a busca for nesse contexto.
   */
  private _handleSuggestionTrigger(textBeforeCursor: string, context?: HTMLElement): void {
    const triggerInfo = this._findTriggerInfo(textBeforeCursor);
    const lastCloseBrace = textBeforeCursor.lastIndexOf('}}');

    // Um gatilho é válido se for encontrado e estiver após o último `}}` (para evitar acionamento dentro de um chip).
    // Em um contexto de argumento, essa verificação é ignorada.
    if (triggerInfo && triggerInfo.position > (context ? -1 : lastCloseBrace)) {
      const currentTerm = textBeforeCursor.slice(
        Math.max(0, triggerInfo.position + triggerInfo.char.length),
      );
      this.suggestionService.trigger.set({ ...triggerInfo, context });
      this.suggestionService.isDropdownVisible.set(true);
      this.suggestionService.fetchSuggestions(currentTerm, (results) => {
        // Atualiza o editor com as novas variáveis encontradas para o parsing.
        this.editorService.updateKnownVariables(results);
      });
    } else {
      this.suggestionService.closeDropdown();
    }
  }

  /**
   * Calcula e define a posição do dropdown de sugestões com base na posição do cursor.
   */
  private updateDropdownPosition(): void {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    const range = selection.getRangeAt(0).cloneRange();
    let rect: DOMRect;

    // Se o cursor estiver colapsado (sem texto selecionado), insere um elemento temporário
    // para obter as coordenadas precisas do cursor.
    if (range.collapsed) {
      const tempElement = document.createElement('span');
      tempElement.textContent = '\u200B'; // Espaço de largura zero
      range.insertNode(tempElement);
      rect = tempElement.getBoundingClientRect();
      tempElement.remove();
    } else {
      rect = range.getBoundingClientRect();
    }

    // Posição absoluta na viewport (abaixo do cursor)
    const top = rect.bottom;
    let left = rect.left;

    // Impede que o dropdown transborde horizontalmente na viewport.
    const dropdownWidth = 384; // Corresponde à classe `sm:w-96` do Tailwind.
    const viewportWidth = window.innerWidth;
    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth;
    }

    if (left < 0) left = 0;

    this.suggestionService.dropdownPosition.set({
      top: `${top}px`,
      left: `${left}px`,
      // Se necessário, adicione position: 'fixed' no estilo do dropdown
    });
  }

  /**
   * Encontra o último gatilho válido no texto.
   *
   * @param text O texto a ser pesquisado.
   * @returns As informações do gatilho (posição, caractere) ou `null`.
   */
  private _findTriggerInfo(text: string): { char: string; position: number } | null {
    let triggerInfo: { char: string; position: number } | null = null;
    for (const char of this.triggerChars()) {
      const pos = text.lastIndexOf(char);
      if (pos > (triggerInfo?.position ?? -1)) {
        triggerInfo = { position: pos, char };
      }
    }

    return triggerInfo;
  }
}
